import {
    Box,
    Typography,
    useTheme,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { Moment } from 'moment';
import PortalCard from '../../../../../components/PortalCard';
import { productAnalyticsStore } from '../../../../../app/stores/ProductAnalytics';
import { ProductWithViews } from '../../../../../models/Analytics';
import { useStoreConfig } from '../../../../../contexts/StoreConfigContext';

interface ProductViewsTableProps {
    dateRange?: [Moment, Moment];
}

const ProductViewsTable: React.FC<ProductViewsTableProps> = ({ dateRange }) => {
    const theme = useTheme();
    const { config } = useStoreConfig();

    const {
        productsWithViews,
        productsWithViewsLoading,
        productsWithViewsError,
        fetchProductsWithViews
    } = productAnalyticsStore((state) => ({
        productsWithViews: state.productsWithViews,
        productsWithViewsLoading: state.productsWithViewsLoading,
        productsWithViewsError: state.productsWithViewsError,
        fetchProductsWithViews: state.fetchProductsWithViews
    }));

    useEffect(() => {
        const startDate = dateRange?.[0]?.toISOString();
        const endDate = dateRange?.[1]?.toISOString();
        fetchProductsWithViews(startDate, endDate);
    }, [dateRange, fetchProductsWithViews]);

    const products = productsWithViews;
    const loading = productsWithViewsLoading;
    const error = productsWithViewsError;

    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState<keyof ProductWithViews>('totalViews');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleRequestSort = (property: keyof ProductWithViews) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedProducts = useMemo(() => {
        return [...products].sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;
            
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return order === 'asc' ? aValue - bValue : bValue - aValue;
            }
            
            const aStr = String(aValue);
            const bStr = String(bValue);
            return order === 'asc' 
                ? aStr.localeCompare(bStr)
                : bStr.localeCompare(aStr);
        });
    }, [products, order, orderBy]);

    const paginatedProducts = useMemo(() => {
        return sortedProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedProducts, page, rowsPerPage]);

    return (
        <PortalCard
            sx={{
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Box sx={{ mb: 2 }}>
                <Typography variant='h6' fontFamily='primaryFont' fontWeight='bold' sx={{ mb: 0.5 }}>
                    All Products and Their Views
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                    Complete product view statistics with unique visitor counts
                </Typography>
            </Box>

            <Box sx={{ flexGrow: 1, width: '100%' }}>
                {loading ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography variant='body2' color='error'>
                            {error}
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper} variant='outlined' sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'productName'}
                                            direction={orderBy === 'productName' ? order : 'asc'}
                                            onClick={() => handleRequestSort('productName')}
                                        >
                                            Product Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'price'}
                                            direction={orderBy === 'price' ? order : 'asc'}
                                            onClick={() => handleRequestSort('price')}
                                        >
                                            Price
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Discount</TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'stockOnHand'}
                                            direction={orderBy === 'stockOnHand' ? order : 'asc'}
                                            onClick={() => handleRequestSort('stockOnHand')}
                                        >
                                            Stock
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'totalViews'}
                                            direction={orderBy === 'totalViews' ? order : 'asc'}
                                            onClick={() => handleRequestSort('totalViews')}
                                        >
                                            Total Views
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel
                                            active={orderBy === 'uniqueViews'}
                                            direction={orderBy === 'uniqueViews' ? order : 'asc'}
                                            onClick={() => handleRequestSort('uniqueViews')}
                                        >
                                            Unique Views
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>Last Viewed</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedProducts.map((product) => (
                                    <TableRow key={product.productId} hover>
                                        <TableCell>{product.productName}</TableCell>
                                        <TableCell>
                                            <Chip label={product.category} size='small' variant='outlined' />
                                        </TableCell>
                                        <TableCell>R{product.price?.toFixed(2) || '0.00'}</TableCell>
                                        <TableCell>
                                            {product.discount 
                                                ? (product.discountType === 'value' 
                                                    ? `${config.currency}${product.discount} OFF`
                                                    : `${product.discount}%`)
                                                : '-'}
                                        </TableCell>
                                        <TableCell>{product.stockOnHand}</TableCell>
                                        <TableCell>{product.totalViews}</TableCell>
                                        <TableCell>{product.uniqueViews}</TableCell>
                                        <TableCell>
                                            {product.lastViewed 
                                                ? new Date(product.lastViewed).toLocaleDateString() + ' ' + 
                                                  new Date(product.lastViewed).toLocaleTimeString()
                                                : '-'
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component='div'
                            count={products.length}
                            page={page}
                            onPageChange={(_, newPage) => setPage(newPage)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                        />
                    </TableContainer>
                )}
            </Box>
        </PortalCard>
    );
};

export default ProductViewsTable;

